import { clsx } from 'clsx';
import { ProgressBar } from '@/shared/ui';
import type { VerifiedField } from '../api/verificationApi';
import { confidenceLevel, confidencePct, type ConfidenceLevel } from '../utils/format';

const CONFIDENCE_COLOR: Record<ConfidenceLevel, string> = {
  high: 'var(--success)',
  medium: 'var(--warning)',
  low: 'var(--error)',
};
import { NeedsMarker, SourceChip } from './chips';
import styles from './VerifiedValueCard.module.css';

interface VerifiedValueCardProps<T> {
  label: string;
  field: VerifiedField<T>;
  format: (value: T) => string;
  /** Replaces the confidence line when set (e.g. "used $0 · remaining $1,500"). */
  sub?: string;
  /** Marker shown when the value is empty. Defaults to "needs call". */
  emptyMarker?: 'needs call' | 'not returned';
}

export function VerifiedValueCard<T>({
  label,
  field,
  format,
  sub,
  emptyMarker = 'needs call',
}: VerifiedValueCardProps<T>) {
  const hasValue = field.value !== null && field.value !== undefined;
  const pct = confidencePct(field.confidence);
  const level = confidenceLevel(field.confidence);

  return (
    <div className={styles.card}>
      <div className={styles.label}>{label}</div>
      <div className={styles.valueRow}>
        {hasValue ? (
          <>
            <span className={styles.value}>{format(field.value as T)}</span>
            <SourceChip source={field.source} />
          </>
        ) : (
          <>
            <span className={clsx(styles.value, styles.valueEmpty)}>—</span>
            <NeedsMarker tone={emptyMarker === 'needs call' ? 'warning' : 'muted'}>
              {emptyMarker}
            </NeedsMarker>
          </>
        )}
      </div>
      {hasValue &&
        (sub ? (
          <div className={styles.conf}>{sub}</div>
        ) : (
          <div className={styles.conf}>
            confidence
            <ProgressBar
              value={pct}
              height={4}
              color={CONFIDENCE_COLOR[level]}
              className={styles.confTrack}
            />
            {pct}%
            {field.reviewed && <span className={styles.reviewed}>· reviewed</span>}
          </div>
        ))}
    </div>
  );
}
